'use client';

import { ReactNode, useRef, useState } from 'react';

import { Slider, SliderHandle, SnapDirection } from '@/rsc/client';

import { SliderControls } from './SliderControls';

type Props = {
  /** Server-rendered slides. */
  children: ReactNode;
  gapPX?: number;
  fullBleed?: boolean;
  snapDirection?: SnapDirection;
  snapDirectionFromXs?: SnapDirection;
  controlsVariant: 'dark' | 'light';
  /** Slider classes (must not override the rail's margins/paddings). */
  sliderClassName?: string;
  controlsClassName?: string;
};

/**
 * The `Slider` + `SliderControls` pairing both DynamicSliderWithVideo and DynamicYourTurnSection
 * used: scroll state from `onScrollChange`, arrows only while the track overflows (both flags true
 * = every slide fully visible). Slides stay server components (children).
 */
export const SliderRail = ({
  children,
  gapPX,
  fullBleed,
  snapDirection,
  snapDirectionFromXs,
  controlsVariant,
  sliderClassName,
  controlsClassName,
}: Props) => {
  const sliderRef = useRef<SliderHandle>(null);
  const [scrollState, setScrollState] = useState({ isAtStart: true, isAtEnd: false });
  const isSliderOverflowing = !(scrollState.isAtStart && scrollState.isAtEnd);

  return (
    <>
      <Slider
        ref={sliderRef}
        gapPX={gapPX}
        fullBleed={fullBleed}
        snapDirection={snapDirection}
        snapDirectionFromXs={snapDirectionFromXs}
        onScrollChange={setScrollState}
        className={sliderClassName}
      >
        {children}
      </Slider>
      {isSliderOverflowing && (
        <SliderControls
          onSlidePrev={() => sliderRef.current?.onPrev()}
          onSlideNext={() => sliderRef.current?.onNext()}
          disablePrev={scrollState.isAtStart}
          disableNext={scrollState.isAtEnd}
          variant={controlsVariant}
          className={controlsClassName}
        />
      )}
    </>
  );
};
